import {
  Channel,
  Episode,
  PlayableMediaImages,
  Season,
  Show,
  Video,
  ShowVideos,
  ShowNextEpisodes,
  EpgProgram,
  EpgChannel,
  ChannelImages,
  DrmMediaItem,
  LiveEvent,
  LiveEventStream,
  ShowDetails,
} from 'types/api/media';
import { AppData } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import {
  diffInHours,
  diffInMinutes,
  getClosestThirtyMinuteOffsetDate,
  hoursToMilliseconds,
} from 'support/timeUtils';
import {
  buildPlayableImageArray,
  getChannelThumbnailUrl,
  getProgramThumbnailUrl,
} from 'support/cwImageUtils';
import { Config, CtvCarouselItem } from 'types/api/config';
import BrightcoveDrmService from 'services/drm/BrightcoveDrmService';

export enum CwErrors {
  NOT_FOUND = 'Video not found',
}

export type CwResponse = {
  result: 'ok' | 'error';
  msg?: CwErrors | string; // Note that msg is only for errors
  [k: string]: any;
};

type BaseLaneItem = {
  isTivoItem?: true;
  isContinueItem?: boolean;
  customImageUrl?: '';
};
export type ChannelLaneItem = BaseLaneItem & Channel;
export type ContentHubLaneItem = BaseLaneItem & ContentHub;
export type EpisodeLaneItem = BaseLaneItem & Episode;
export type LiveEventLaneItem = BaseLaneItem & LiveEvent;
export type PromoLaneItem = BaseLaneItem & Promo;
export type ShowLaneItem = BaseLaneItem & Show;
export type VideoLaneItem = BaseLaneItem & Video;

export type LaneItem<
  LaneItemType =
    | ChannelLaneItem
    | ContentHubLaneItem
    | EpisodeLaneItem
    | LiveEventLaneItem
    | ShowLaneItem
    | VideoLaneItem,
> = LaneItemType;

// Only supporting `Episode`, `Channel` and `Show` Tivo lanes
export type TivoLaneItem<
  LaneItemType = EpisodeLaneItem | ChannelLaneItem | ShowLaneItem,
> = LaneItemType;

export type Lane = {
  items: LaneItem[];
  itemType: 'event' | 'show' | 'video' | 'channel' | 'content-hub';
  laneType:
    | 'promos'
    | 'continue-watching'
    | 'show-group'
    | 'tivo-api-screens'
    | 'channels'
    | 'latest-episodes'
    | 'events-list';
  presentation:
    | 'promo'
    | 'horizontal'
    | 'vertical'
    | 'square'
    | 'vertical-expanding'
    | 'horizontal-expanding'
    | 'horizontal-episodic'
    | 'cw-bezel';
  slug: string;
  title: string;
  apiUrl?: string;
};

export type PromoLane = Omit<Lane, 'items' | 'itemType'> & {
  items: PromoLaneItem[];
  itemType: 'promo';
};

export type ContentHubBackgroundImages = {
  hubBackgroundHorizontal?: string;
  hubBackgroundVertical?: string;
  hubBackgroundSquare?: string;
};

export type Swimlanes = {
  lanes: (Lane | PromoLane)[];
  lanesCount: number;
  layout: 'carousels' | 'grid';
  title: string;
  titleDisplayed: number;
  backgroundImages: ContentHubBackgroundImages;
  backgroundColor: string;
};

export type LoginLandingLane = CtvCarouselItem & {
  items: CtvCarouselItem[];
  type: 'login-landing';
};

export type ContentHub = {
  id: string;
  slug: string;
  title: string;
  images: { thumbnail: string };

  // internal
  type: 'content-hub';
};

export type PromoChannel = {
  id: string;
  title: string;
  slug: string;
  // internal
  type: 'PromoChannel';
};

export type Promo = (Show | Video | LiveEvent | ContentHub | PromoChannel) & {
  promoImageId: string;
  promoButton: string;
  promoDescription: string;
};

export type WithProgress<T> = T & { progress?: number; isComplete?: boolean };

const season = (data: any): Season => {
  return {
    id: data.availability_season_id,
    title: data.title,
    code: data.code,
    header: data.header,
    episodeCount: data.episode_count,
  };
};

const HubBackgroundImages = (data: any): ContentHubBackgroundImages => {
  return {
    hubBackgroundHorizontal: data['hub-background-horizontal'],
    hubBackgroundVertical: data['hub-background-vertical'],
    hubBackgroundSquare: data['hub-background-square'],
  };
};

// build images with dynamic urls, and only return the ones that are used
const buildImagesArray = (data: any): PlayableMediaImages => {
  const slug =
    data.slug ||
    data.showSlug ||
    data.show_slug ||
    data.event_slug ||
    data.metadata?.slug ||
    data.metadata?.showSlug;
  if (!slug) return {};

  const guid = data.guid || '';
  const badgeId = data.badge_id || data.metadata?.badgeId || '';
  return buildPlayableImageArray(slug, guid, badgeId);
};

export const getEpisode = (
  showSlug: string,
  seriesName: string,
  data: any,
): Episode | null => {
  if (!data) return null;

  return {
    airDate: data.airdate,
    description: data.description_long,
    duration: data.duration,
    durationSecs: data.duration_secs || data.durationsecs,
    episode: data.episode,
    episodeInSeason: data.episode_in_season,
    guid: data.guid,
    images: buildImagesArray(data),
    season: data.season,
    title: data.title,

    // Internal
    type: 'Episode',
    showSlug,
    seriesName,
  };
};

const video = (data: any): Video | null => {
  if (!data || Object.keys(data).length === 0) return null;

  if (data.error) {
    console.error(['[CWData] video parser error', data]);
    return null;
  }

  const showSlug = data.show_slug ?? data.showSlug;
  const seriesName = data.series_name;

  return {
    title: data.title,
    description: data.description_long,
    id: data.guid,
    guid: data.guid,
    rating: data.rating,
    adZone: data.adZone,
    airDate: data.airdate,
    bcVideoId: data.bc_video_id,
    bifSdUrl: data.image_bif?.sd,
    bifHdUrl: data.image_bif?.hd,
    creditSecs: data.credits_secs,
    expireTime: data.expire_time,
    episode: data.episode,
    episodeInSeason: data.episode_in_season,
    duration: data.duration,
    durationSecs: data.duration_secs,
    genre: data.comscore_genre,
    imdbGenre: data.imdb_genres,
    isFullEpisode: data?.fullep === 1 ? true : false,
    images: buildImagesArray(data),
    isNew: data.is_new,
    mpxUrl: data.mpx_url,
    nextEpisode: getEpisode(showSlug, seriesName, data.next_episode),
    nielsenGenre: data.nielsen_genre,
    season: data.season,
    series: data.series,
    seriesName,
    seriesSlug: data.series_slug,
    seriesType: data.series_type,
    showSlug: showSlug,
    startTime: data.start_time,
    tmsId: data.tms_id,

    // used for Conviva (see analytics spreadsheet)
    contentType: data?.overlay ?? data?.type ?? '',
    brand: data.show_type,

    // Internal
    isLive: false,
    type: 'Video',
  };
};

const featuredTrailer = (data: any) => {
  return {
    id: data.video_guid,
    video: video(data.video_item),
    trailerUrl: data.video_mp4,
  };
};

const show = (data: any): Show | null => {
  if (!data || Object.keys(data).length === 0) return null;

  const images = buildImagesArray(data);
  const firstEpisode = getEpisode(data.slug, data.title, data.first_episode);
  return {
    title: data.title,
    description: data.summary_long ?? data.summary,
    rating: data.rating,
    id: data.slug,
    type: data.type,
    releaseYear: data.release_year,
    images,
    firstEpisode,
    genres: data.imdb_genres,
    totalSeasons: data.season_count || 0,
    showSlug: data.slug,
  };
};

const channel = (data: any): Channel | null => {
  if (!data || Object.keys(data).length === 0) return null;

  return {
    id: data.slug,
    description: data.description,
    title: data.title,
    images: getChannelImages(data),
    slug: data.slug,

    // Internal
    type: 'LiveChannel',
  };
};

const epgChannel = (data: any): EpgChannel => {
  const programs = (data.programs ?? []).map(epgProgram);
  const drmItems = (data.drm_media_items ?? []).map(drmMediaItem);

  return {
    adZone: data.ad_zone,
    id: data.slug,
    images: getChannelImages(data),
    genre: data.genre,
    programs,
    streamUrl: data.stream_url,
    streamType: data.stream_type,
    slug: data.slug,
    isDrm: data.has_drm === 1,
    drmItems,
    analytics: {
      genre: data.genre,
      guid: data.analytics_guid,
      slug: data.slug,
      title: data.title,
    },

    isLive: true,
    title: data.title,
    type: 'EpgChannel',
  };
};

const getChannelImages = (data: any): ChannelImages => {
  return {
    homeThumbnail: getChannelThumbnailUrl(data.slug, data.badge_id),
    programThumbnail: getProgramThumbnailUrl(data.slug, data.badge_id),
    logoFocused: data.icon_focused_url,
    logoUnfocused: data.icon_unfocused_url,
    showcaseBackground: data.showcase_image_url,
  };
};

const epgProgram = (data: any): EpgProgram => {
  return {
    assetId: data.asset_id,
    category: data.category,
    convivaAssetName: data.conviva_asset_name,
    description: data.description,
    durationSecs: data?.duration_secs?.toString() ?? '',
    images: {
      iconUrl: data.icon_url,
      detailsUrl: data.details_image_url,
    },
    endTime: data.end_time,
    isOnDemand: data.on_demand,
    ratings: data.ratings,
    seriesSlug: data.series_slug,
    seriesType: data.series_type,
    startTime: data.start_time,
    subtitle: data.subtitle,
    title: data.title,
    genre: data.genre,
    season: data.season,
    episode: data.episode,
  };
};

const drmMediaItem = (data: any): DrmMediaItem => {
  return {
    mimeType: data.mimetype,
    pid: data.pid,
    selectorUrl: data.selector_url,
    streamUrl: data.streaming_url,
  };
};

/**
 * Cleanses the channel programming data. It will:
 * 1. Fill channels with empty programming data with placeholder programs
 * 2. Remove programs that have already expired (Finished before start time)
 * 3. Add placeholder programs before start time if necessary
 *
 * @param channels Channels we want to clean up
 * @param epgStartTime Should be a 30 minute interval (I.e. 12:00, 12:30, 1:00, 1:30, etc.).
 * If not, it will get converted
 *
 * @returns The cleansed channel data
 */
export const cleanupEpgChannelData = (
  channels: EpgChannel[],
  epgStartTime: Date,
): EpgChannel[] => {
  // Ensure the start date is in 30 minute interval format
  getClosestThirtyMinuteOffsetDate(epgStartTime);

  channels.forEach((channel: EpgChannel) => {
    let programs = channel.programs;
    const channelTitle = channel.title;

    if (programs.length) {
      programs = filterExpiredEpgPrograms(programs, epgStartTime);
      const firstItemStartTime = new Date(programs[0]!.startTime);

      if (firstItemStartTime > epgStartTime) {
        const programPadding = fillPlaceholderPrograms(
          channelTitle,
          epgStartTime,
          firstItemStartTime,
        );
        programs.unshift(...programPadding);
      }
    } else {
      programs = fillPlaceholderPrograms(channelTitle, epgStartTime);
    }
    channel.programs = programs;
  });

  return channels;
};

export const filterExpiredEpgPrograms = (
  programs: EpgProgram[],
  epgStartTime: Date,
): EpgProgram[] => {
  return programs.filter((program: EpgProgram) => {
    const programEndTime = new Date(program.endTime);
    return epgStartTime < programEndTime;
  });
};

const fillPlaceholderPrograms = (
  channelTitle: string,
  startTime: Date,
  endTime?: Date,
) => {
  const placeholderPrograms: EpgProgram[] = [];

  // Max placeholder hours we can fill (13 to avoid timing issues around half hour)
  const MAX_HOURS = 13;
  const MAX_MILLISECONDS = hoursToMilliseconds(MAX_HOURS);
  if (!endTime) endTime = new Date(startTime.getTime() + MAX_MILLISECONDS);

  let hoursToFill = Math.floor(diffInHours(endTime, startTime));
  // Account for cases where a programs start time is > 13 hours away
  if (hoursToFill > MAX_HOURS) hoursToFill = MAX_HOURS;

  // Fill in hours going backwards from the final end date
  while (hoursToFill > 0) {
    const tempStartMilliseconds: number =
      endTime.getTime() - hoursToMilliseconds(1);
    const tempStartTime = new Date(tempStartMilliseconds);

    const placeholderProgram = buildPlaceholderProgram(
      tempStartTime,
      endTime,
      channelTitle,
    );

    placeholderPrograms.unshift(placeholderProgram);
    endTime = tempStartTime;
    hoursToFill -= 1;
  }

  // Should always be 0 - 59 after reducing end date above
  const minutesToFill = diffInMinutes(endTime, startTime);

  if (minutesToFill) {
    const placeholderProgram = buildPlaceholderProgram(
      startTime,
      endTime,
      channelTitle,
    );
    placeholderPrograms.unshift(placeholderProgram);
  }

  return placeholderPrograms;
};

const buildPlaceholderProgram = (
  startTime: Date,
  endTime: Date,
  channelTitle: string,
): EpgProgram => {
  const program = epgProgram({});
  program.category = 'epg_filler';
  program.isPlaceholder = true;
  program.isOnDemand = false;
  program.title = translate('epg.fallbackTitle');
  program.subtitle = '';

  program.startTime = startTime.toISOString();
  program.endTime = endTime.toISOString();
  program.durationSecs = (
    (endTime.getTime() - startTime.getTime()) /
    1000
  ).toString();

  program.convivaAssetName = translate(
    'epg.fallbackConvivaAssetName',
    channelTitle,
    program.title,
    program.startTime,
  );
  return program;
};

const liveEvent = (
  data: any,
  overlays: LiveEvent['overlays'],
): LiveEvent | null => {
  if (!data || Object.keys(data).length === 0) return null;

  const analytics = data.analytics ?? {};
  return {
    title: data.event_name,
    slug: data.event_slug,
    groupSlug: data.event_group_slug ?? data.group_slug,
    description: data.event_description,
    tuneIn: data.event_tunein,
    startDatePage: data.start_date_page,
    endDatePage: data.end_date_page,
    endDateAccess: data.end_date_access,
    startDateStream: data.start_date_stream,
    endDateStream: data.end_date_stream,
    id: data.internal_slug,
    images: {
      eventThumbnail: data.banners.event_thumbnail,
      liveStreamBackground: data.banners.livestream_bg,
      eventLockup: data.banners.event_lockup,
      largeBillboard: data.banners['billboard-1920x1080'],
      mediumBillboard: data.banners['billboard-1920x360'],
      smallBillboard: data.banners['billboard-1920x768'],
      eventThumbnailTemplate: data.banners.event_thumbnail_url_template,
    },
    overlays,
    has_dvr: data.has_dvr,
    hist: data.hist,

    // Live unique fields
    analytics: {
      comscoreGenre: analytics.comscore_genre,
      guid: analytics.video_guid,
      nielsenGenre: analytics.nielsen_genre,
      seriesCode: analytics.series_code,
      slug: analytics.show_slug,
      title: analytics.show_title,
      videoRating: analytics.video_rating,
    },

    // Internal
    isLiveEvent: true,
    type: 'LiveEvent',
  };
};

const liveEventStream = (data: any): LiveEventStream | null => {
  const event = liveEvent(data, data.overlays);
  if (!event) return null;

  return {
    ...event,

    // Unique to LiveEventStream
    adZone: data.ad_zone,
    streamUrl: data.stream_url,
    durationSecs: data.duration_secs,
    preplayUrl: data.preplay,
    customParams: data.cust_params,
    preplayParams: data.preplay_params,
    internalSlug: data.internal_slug,
    eventKeywords: data.event_keywords,

    // Internal
    type: 'LiveEventStream',
  };
};

const contentHub = (item: any): ContentHub | null => {
  if (!item || Object.keys(item).length === 0) return null;

  const thumbnail = buildContentHubImage(item);

  return {
    id: item.slug ?? '',
    slug: item.slug ?? '',
    title: item.title ?? '',
    images: { thumbnail },

    // Internal
    type: 'content-hub',
  };
};

const buildContentHubImage = (item: any) => {
  const baseUrl = AppData?.images.dynamicBaseUrl ?? '';
  const template = item?.image_url_template ?? '';

  return `${baseUrl}${template}`;
};

const promoChannel = (data: any): PromoChannel | null => {
  if (!data || Object.keys(data).length === 0) return null;
  const slug = data.channel_slug || data.slug || data.show_slug;
  return {
    id: slug,
    title: data.title,
    slug,
    // Internal
    type: 'PromoChannel',
  };
};

const promo = (data: any): Promo | null => {
  let content;
  switch (data.promo_type) {
    case 'show':
      content = show(data);
      break;
    case 'video':
      content = video(data);
      break;
    case 'liveplayer':
      content = liveEvent(data.live_event, data.overlays);
      break;
    case 'content-hub':
      content = contentHub(data);
      break;
    case 'channel':
      content = promoChannel(data);
      break;
    default:
      content = null;
  }

  if (content === null) {
    return null;
  }

  const result = {
    ...content,
    promoImageId: data.promo_image_id as string,
    promoButton: data.promo_button as string,
    promoDescription: data.promo_description as string,
  };

  return result;
};

const createLaneItem = (
  item: any,
  itemType: Lane['itemType'] | PromoLane['itemType'],
) => {
  switch (itemType) {
    case 'event':
      return liveEvent(item.event, item.overlays);
    case 'show':
      return show(item);
    case 'video':
      return video(item);
    case 'promo':
      return promo(item);
    case 'channel':
      return channel(item);
    case 'content-hub':
      return contentHub(item);
    default:
      return null;
  }
};

export const parseLane = (data: any): Lane | undefined => {
  if (!data || Object.keys(data).length === 0) return undefined;
  const itemType = data.item_type;
  const laneType = data.type;
  if (!itemType || !laneType) return undefined;

  const { presentation, slug, title } = data;
  const items: Array<LaneItem> = data.items
    .map((itemData: any) => {
      return createLaneItem(
        { ...itemData, ...(data.overlays && { overlays: data.overlays }) },
        itemType,
      );
    })
    .filter((itemData: LaneItem | undefined) => itemData);

  return {
    items,
    itemType,
    laneType,
    presentation,
    slug,
    title,
    apiUrl: data.api_url,
  };
};

export const parseSwimlanes = (data: any): Swimlanes | null => {
  if (!data || Object.keys(data).length === 0) return null;
  const {
    lanes_count: lanesCount,
    layout,
    title,
    title_displayed: titleDisplayed,
    background_images: background_images,
    background_color: backgroundColor,
  } = data;
  const lanes = data.lanes
    .map(parseLane)
    .filter((lane: Lane | undefined) => lane);

  return {
    lanes,
    lanesCount,
    layout: layout || 'carousels',
    title,
    titleDisplayed,
    backgroundImages: HubBackgroundImages(background_images),
    backgroundColor,
  };
};
export const parseShowDetails = (data: any): ShowDetails | null => {
  if (!Array.isArray(data.items) || data.items.length === 0) return null;
  const content = data.items[0];

  const baseShowData = show(content);
  if (!baseShowData) return null;

  const featuredTrailerData = content.featured_trailer?.video_item
    ? featuredTrailer(content.featured_trailer)
    : null;
  const seasons = content.seasons
    ?.filter((seasonData: any) => {
      return seasonData?.availability_season_id && seasonData?.episode_count;
    })
    .map((seasonData: any) => season(seasonData));

  return {
    ...baseShowData,
    activeYears: content.active_years,
    featuredTrailer: featuredTrailerData,
    showCast: content.show_cast,
    showProducers: content.show_producers,
    showDirectors: content.show_directors,
    seasons,
  };
};

export const parseShowVideos = (data: any): ShowVideos => {
  const { next_episodes, title, items } = data;

  const nextEpisodes: ShowNextEpisodes[] =
    next_episodes?.map((episode: any) => {
      const {
        date,
        caption,
        episode_title: episodeTitle,
        episode_number: episodeNumber,
      } = episode;

      return {
        date,
        caption,
        episodeTitle,
        episodeNumber,
      };
    }) ?? [];

  return {
    items: parseVideoMap(items),
    nextEpisodes,
    title,
  };
};
export const parseVideo = (data: any): Video | null => {
  return video(data.video);
};
export const parseVideos = (data: any): Array<Video> => {
  return parseVideoMap(data.videos);
};
const parseVideoMap = (videos: any) => {
  return videos.map(video).filter((video: Video) => !!video);
};

export const parseLiveEvent = (data: any): LiveEventStream | null => {
  const liveEventData = { ...data.items[0], overlays: data.overlays };
  return liveEventStream(liveEventData);
};

export const parseLiveEvents = (data: any): LiveEventStream[] | null => {
  const overlays = data.overlays;
  return data.items
    .map((item: any) => {
      const liveEventData = { ...item, overlays };
      return liveEventStream(liveEventData);
    })
    .filter((liveEvent: LiveEventStream | null) => !!liveEvent);
};

export const parseEpgChannels = (data: any) => {
  const channels = data.channels.map(epgChannel) as EpgChannel[];
  const maxChannels = data.channel_count;
  return { channels, maxChannels };
};

export const parseVideoPlatform = (config: Config) => {
  const videoPlatform = config['ad-params'].platform.video_platform;
  const [platform, contentSourceId] = videoPlatform.split('-');

  // Only supporting Brightcove for now
  const drmService = new BrightcoveDrmService();

  return { contentSourceId, drmService };
};
